// ─────────────────────────────────────────────────────────────────────────────
// UTM·광고 어트리뷰션 캡처 (ALLIBEE-356)
//
// 광고 랜딩 URL로 들어온 UTM/클릭ID 파라미터와 방문 컨텍스트를 localStorage에 보관한다.
// - first-touch: 최초 방문 1회만 기록(이미 있으면 유지) → PostHog $set_once 부착·가입 payload BE 전송(추후)에 사용
// - last-touch : 어트리뷰션 파라미터가 있는 방문마다 갱신(어트리뷰션 모델 비교용).
//                direct 재방문이 마지막 캠페인 정보를 지우지 않도록 파라미터 없는 방문은 덮지 않는다.
// - first-ad   : 광고 유입(utm_source google·naver)이 처음 확인된 방문 1회만 기록(ALLIBEE-1190).
//                first-touch가 organic이면 영구 빈값이라, 자연 유입 후 광고로 재방문한 가입을 광고에 귀속시키기 위한 별도 키.
//                first-touch를 덮어쓰면 자연 유입이 광고로 잡히므로 first-touch는 건드리지 않는다.
// PostHog에는 큐레이션 subset(utm 5종·gclid·referrer·landing_page·seen_at)만 올리고,
// 원시 클릭 ID(nclid·NaPm·gbraid·gad_*·matchtype)는 저장만 한다(광고 전환 매칭은 BE/BigQuery에서).
// ─────────────────────────────────────────────────────────────────────────────

const FIRST_TOUCH_KEY = 'allibee:attribution:first';
const LAST_TOUCH_KEY = 'allibee:attribution:last';
const FIRST_AD_TOUCH_KEY = 'allibee:attribution:firstAd';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const AD_CLICK_PARAMS = ['matchtype', 'gad_source', 'gad_campaignid', 'gbraid', 'gclid', 'nclid', 'NaPm'] as const;

// Growth 기준 광고 유입 utm_source. 통합 앨리비 메인 경유(allibee_main) 등 내부 유입 utm은 광고가 아니다.
const AD_UTM_SOURCES = new Set(['google', 'naver']);
// 크로스 오리진 핸드오프에서 first-ad utm을 실어 보내는 쿼리 접두어 — first-touch용 utm_* 과 섞이면 도착지 first-touch가 광고로 오염된다.
const FORWARDED_FIRST_AD_PREFIX = 'first_ad_';

export type AttributionParamKey = (typeof UTM_PARAMS)[number] | (typeof AD_CLICK_PARAMS)[number];

export interface StoredAttribution {
    /** URL에서 캡처한 utm/광고 파라미터 (존재하는 것만) */
    params: Partial<Record<AttributionParamKey, string>>;
    referrer: string;
    /** 도착 경로 — path만, 쿼리스트링 제외 */
    landingPage: string;
    /** 캡처 시각 (ISO 8601) */
    seenAt: string;
}

function readStored(key: string): StoredAttribution | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        // 형태가 다른 값(수동 조작·과거 실험 잔재 등)은 무시 — 호출부(setPostHogUser → fetchAuthInfo)로 예외가
        // 전파되면 인증 흐름까지 깨질 수 있으므로 어트리뷰션은 조용히 포기한다.
        if (typeof parsed !== 'object' || parsed === null) return null;
        const candidate = parsed as Partial<StoredAttribution>;
        if (typeof candidate.params !== 'object' || candidate.params === null) return null;
        return candidate as StoredAttribution;
    } catch {
        // localStorage 접근 불가(SSR·프라이빗 모드) 또는 손상된 JSON — 어트리뷰션은 best-effort
        return null;
    }
}

function isAdVisit(params: StoredAttribution['params']): boolean {
    const source = params.utm_source?.toLowerCase();
    return source !== undefined && AD_UTM_SOURCES.has(source);
}

/** forwardAttributionUtm이 실어 보낸 first-ad utm(first_ad_utm_*)을 도착지 URL에서 복원한다. */
function readForwardedFirstAd(url: URL, snapshot: StoredAttribution): StoredAttribution | null {
    const params: StoredAttribution['params'] = {};
    for (const key of UTM_PARAMS) {
        const value = url.searchParams.get(`${FORWARDED_FIRST_AD_PREFIX}${key}`);
        if (value) params[key] = value;
    }
    return isAdVisit(params) ? { ...snapshot, params } : null;
}

/**
 * 앱 최초 로드 시 1회 호출해 first/last/first-ad 어트리뷰션을 localStorage에 기록한다.
 * first-touch는 파라미터가 없는 organic 방문도 기록한다(referrer/landing/seenAt).
 */
export function captureAttribution(url: URL, referrer: string): void {
    const params: StoredAttribution['params'] = {};
    for (const key of [...UTM_PARAMS, ...AD_CLICK_PARAMS]) {
        const value = url.searchParams.get(key);
        if (value) params[key] = value;
    }

    const snapshot: StoredAttribution = {
        params,
        // referrer는 원본 그대로 저장(티켓 명세: document.referrer) — 크로스 오리진은 브라우저 Referrer-Policy가 이미 origin으로 제한
        referrer,
        landingPage: url.pathname,
        seenAt: new Date().toISOString(),
    };

    try {
        if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
            localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(snapshot));
        }
        if (Object.keys(params).length > 0) {
            localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(snapshot));
        }
        if (!localStorage.getItem(FIRST_AD_TOUCH_KEY)) {
            // 핸드오프로 넘어온 값이 현재 URL의 광고 파라미터보다 앞선 방문이므로 우선한다
            const firstAd = readForwardedFirstAd(url, snapshot) ?? (isAdVisit(params) ? snapshot : null);
            if (firstAd) localStorage.setItem(FIRST_AD_TOUCH_KEY, JSON.stringify(firstAd));
        }
    } catch {
        // localStorage 접근 불가 — 어트리뷰션은 best-effort
    }
}

export function getFirstTouchAttribution(): StoredAttribution | null {
    return readStored(FIRST_TOUCH_KEY);
}

export function getLastTouchAttribution(): StoredAttribution | null {
    return readStored(LAST_TOUCH_KEY);
}

export function getFirstAdTouchAttribution(): StoredAttribution | null {
    return readStored(FIRST_AD_TOUCH_KEY);
}

/**
 * 광고 유입 시점(first-touch)의 utm_content — 기능별 광고→가입 전환 매칭용 이름표 (ALLIBEE-1173).
 * Growth 명세: 가공·정규화 없이 원본 그대로, 없으면 빈값.
 */
export function getFirstTouchFeature(): string {
    return getFirstTouchAttribution()?.params.utm_content ?? '';
}

/**
 * 광고 유입이 처음 확인된 시점(first-ad)의 utm_content (ALLIBEE-1190). 원본 그대로, 없으면 빈값.
 */
export function getFirstAdTouchFeature(): string {
    return getFirstAdTouchAttribution()?.params.utm_content ?? '';
}

/**
 * 통합 회원가입 크로스 도메인 핸드오프(GEN-702 앨리비→큐)용 — localStorage 어트리뷰션은 오리진을
 * 넘어가지 못하므로, 대상 URL 쿼리로 utm을 실어 도착지의 captureAttribution이 이어받게 한다.
 * - first-touch utm은 utm_* 그대로. 대상 URL에 이미 있는 키는 덮지 않는다(광고가 가입 경로에 직접 착지한 경우 현재 URL 값 우선).
 * - first-ad utm은 first_ad_utm_* 접두어로 따로 실어 도착지 first-touch를 오염시키지 않는다.
 * 클릭 ID(gclid 등)는 광고 클릭 중복 집계 여지가 있어 전달하지 않는다.
 */
export function forwardAttributionUtm(target: URL): void {
    const first = getFirstTouchAttribution();
    for (const key of UTM_PARAMS) {
        const value = first?.params[key];
        if (value && !target.searchParams.has(key)) target.searchParams.set(key, value);
    }
    const firstAd = getFirstAdTouchAttribution();
    for (const key of UTM_PARAMS) {
        const value = firstAd?.params[key];
        if (value) target.searchParams.set(`${FORWARDED_FIRST_AD_PREFIX}${key}`, value);
    }
}

/**
 * PostHog person 속성($set_once)용 first-touch 큐레이션 subset.
 * 저장된 first-touch가 없거나 올릴 값이 없으면 undefined.
 */
export function getFirstTouchPosthogProps(): Record<string, string> | undefined {
    const first = getFirstTouchAttribution();
    if (!first) return undefined;

    const props: Record<string, string> = {};
    for (const key of UTM_PARAMS) {
        const value = first.params[key];
        if (value) props[key] = value;
    }
    if (first.params.gclid) props.gclid = first.params.gclid;
    if (first.referrer) props.first_referrer = first.referrer;
    if (first.landingPage) props.landing_page = first.landingPage;
    if (first.seenAt) props.first_seen_at = first.seenAt;

    return Object.keys(props).length > 0 ? props : undefined;
}
